// src/components/notifications/NotificationPreview.jsx
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Divider,
  Alert,
} from '@mui/material';
import {
  Info,
  Warning,
  Error as ErrorIcon,
  CheckCircle,
  Assignment,
  Schedule,
  Build,
  SystemUpdate,
} from '@mui/icons-material';

const getNotificationIcon = (type) => {
  switch (type) {
    case 'info':
      return <Info color="info" />;
    case 'warning':
      return <Warning color="warning" />;
    case 'error':
      return <ErrorIcon color="error" />;
    case 'success':
      return <CheckCircle color="success" />;
    case 'task_assigned':
      return <Assignment color="primary" />;
    case 'task_completed':
      return <CheckCircle color="success" />;
    case 'deadline_reminder':
      return <Schedule color="warning" />;
    case 'system_update':
      return <SystemUpdate color="info" />;
    case 'maintenance':
      return <Build color="warning" />;
    default:
      return <Info color="action" />;
  }
};

export default function NotificationPreview({ formData, recipientCount = 0 }) {
  const { t } = useTranslation();

  const NOTIFICATION_TYPES = useMemo(() => [
    { value: 'info', label: t('notifications:notification_types.info'), color: 'info' },
    { value: 'warning', label: t('notifications:notification_types.warning'), color: 'warning' },
    { value: 'error', label: t('notifications:notification_types.error'), color: 'error' },
    { value: 'success', label: t('notifications:notification_types.success'), color: 'success' },
    { value: 'task_assigned', label: t('notifications:notification_types.task_assigned'), color: 'secondary' },
    { value: 'task_completed', label: t('notifications:notification_types.task_completed'), color: 'success' },
    { value: 'deadline_reminder', label: t('notifications:notification_types.deadline_reminder'), color: 'warning' },
    { value: 'system_update', label: t('notifications:notification_types.system_update'), color: 'info' },
    { value: 'maintenance', label: t('notifications:notification_types.maintenance'), color: 'warning' },
  ], [t]);

  const PRIORITY_LEVELS = useMemo(() => [
    { value: 'low', label: t('notifications:priority.low'), color: 'default' },
    { value: 'normal', label: t('notifications:priority.normal'), color: 'secondary' },
    { value: 'high', label: t('notifications:priority.high'), color: 'warning' },
    { value: 'urgent', label: t('notifications:priority.urgent'), color: 'error' },
  ], [t]);

  const typeInfo = NOTIFICATION_TYPES.find(t => t.value === formData.notification_type);
  const priorityInfo = PRIORITY_LEVELS.find(p => p.value === formData.priority);

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {t('notifications:preview.title')}
        </Typography>
        <Divider sx={{ mb: 2 }} />
        
        {/* Preview Box */}
        <Box sx={{ 
          p: 2, 
          border: 1, 
          borderColor: 'divider', 
          borderRadius: 1,
          backgroundColor: 'action.hover',
          position: 'relative'
        }}>
          {/* Notification Badge */}
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            {getNotificationIcon(formData.notification_type)}
            <Chip
              label={typeInfo?.label || t('notifications:preview.type_placeholder')}
              size="small"
              color={typeInfo?.color || 'default'}
            />
            <Chip
              label={priorityInfo?.label || t('notifications:preview.priority_placeholder')}
              size="small"
              color={priorityInfo?.color || 'default'}
              variant="outlined"
            />
          </Box>
          
          {/* Title */}
          <Typography variant="subtitle1" fontWeight="medium" gutterBottom>
            {formData.title || t('notifications:preview.title_placeholder')}
          </Typography>
          
          {/* Message */}
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {formData.message || t('notifications:preview.message_placeholder')}
          </Typography>
          
          {/* Click URL */}
          {formData.onclick_url && (
            <Typography variant="caption" color="primary.main">
              {t('notifications:preview.click_action')}: {formData.onclick_url}
            </Typography>
          )}
          
          {/* Expiration */}
          {formData.expires_at && (
            <Typography variant="caption" color="warning.main" display="block">
              {t('notifications:preview.expires')}: {formData.expires_at.toLocaleString()}
            </Typography>
          )}

          {/* Timestamp */}
          <Typography variant="caption" color="text.disabled" display="block" mt={1}>
            {t('notifications:preview.just_now')}
          </Typography>
        </Box>

        {/* Delivery Info */}
        {recipientCount > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {t('notifications:preview.delivery_info', { count: recipientCount })}
          </Alert>
        )}

        {/* Validation Warnings */}
        {(!formData.title || !formData.message) && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            {t('notifications:preview.validation_warning')}
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}